// src/pages/Events/components/MultiCameraView.jsx
// 📹 Synchronized multi-camera playback (2x2 grid)

import { useEffect, useRef, useState } from "react";
import { FiMaximize2 } from "react-icons/fi";

export default function MultiCameraView({
  cameras = [],
  currentTime,
  recordings = [],
  playing,
}) {
  // Limit to 4 cameras for 2x2 grid
  const displayCameras = cameras.slice(0, 4);

  return (
    <div className="flex-1 bg-black p-2 grid grid-cols-2 gap-2">
      {displayCameras.map((camera, idx) => (
        <CameraCell
          key={camera.id}
          camera={camera}
          currentTime={currentTime}
          recordings={recordings}
          playing={playing}
          position={idx + 1}
        />
      ))}

      {/* Empty cells if less than 4 cameras */}
      {Array.from({ length: 4 - displayCameras.length }, (_, idx) => (
        <div
          key={`empty-${idx}`}
          className="bg-neutral-900 rounded flex items-center justify-center text-gray-600"
        >
          <div className="text-center">
            <div className="text-sm">Empty Slot</div>
            <div className="text-xs text-gray-700 mt-1">No camera selected</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CameraCell({ camera, currentTime, recordings, playing, position }) {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Find recording for this camera at current time
  const currentRecording = recordings.find(rec => {
    if (rec.camera_id !== camera.id) return false;
    const start = new Date(rec.start_time);
    const end = new Date(rec.end_time);
    return currentTime >= start && currentTime <= end;
  });

  // Load video
  useEffect(() => {
    if (!currentRecording || !videoRef.current) {
      setVideoLoaded(false);
      return;
    }

    const video = videoRef.current;
    video.src = currentRecording.file_url;
    
    // Calculate offset
    const recordingStart = new Date(currentRecording.start_time);
    const offset = (currentTime - recordingStart) / 1000;
    video.currentTime = offset;
    
    setVideoLoaded(true);
    setError(null);

    video.onerror = () => {
      setError("Playback error");
      setVideoLoaded(false);
    };
  }, [currentRecording, currentTime]);

  // Handle playback
  useEffect(() => {
    if (!videoRef.current || !videoLoaded) return;

    if (playing) {
      videoRef.current.play().catch(err => {
        console.error("Playback error:", err);
        setError("Playback failed");
      });
    } else {
      videoRef.current.pause();
    }
  }, [playing, videoLoaded]);

  return (
    <div className="relative bg-neutral-900 rounded overflow-hidden aspect-video">
      {/* Video */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-contain"
        playsInline
        muted
      />

      {/* Camera Label */}
      <div className="absolute top-2 left-2 bg-black/80 backdrop-blur-sm px-3 py-1 rounded text-white text-xs font-medium">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            videoLoaded && currentRecording ? "bg-green-500" : "bg-red-500"
          }`} />
          {camera.name}
        </div>
      </div>

      {/* Position Indicator */}
      <div className="absolute top-2 right-2 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
        {position}
      </div>

      {/* No Recording */}
      {!currentRecording && !error && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <div className="text-sm">No Recording</div>
            <div className="text-xs mt-1">No data for this time</div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center text-red-500">
          <div className="text-center">
            <div className="text-sm">{error}</div>
          </div>
        </div>
      )}

      {/* Loading */}
      {currentRecording && !videoLoaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Recording Info */}
      {currentRecording && (
        <div className="absolute bottom-2 left-2 bg-black/80 backdrop-blur-sm px-2 py-1 rounded text-white text-xs">
          {new Date(currentRecording.start_time).toLocaleTimeString()}
        </div>
      )}

      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors group">
        <button
          onClick={() => {
            // TODO: Focus on this camera (switch to single view)
            console.log("Focus on camera:", camera.id);
          }}
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <div className="p-3 bg-blue-600 rounded-full text-white">
            <FiMaximize2 className="w-6 h-6" />
          </div>
        </button>
      </div>
    </div>
  );
}
